import { DateTime } from 'luxon';
import type { IWorkingHours } from '@interfaces/IWorkingHours';

/**
 * Resolved shift-footer state. Modelled as a discriminated union so the label
 * renderer can only read `range`/`minutesToChange` on the statuses that carry
 * them: `before`/`active` always have a `range`; `before` always has a
 * countdown; `active` has one except for a cross-midnight window (`null`).
 */
export type ShiftState =
  | { status: 'before'; range: string; minutesToChange: number }
  | { status: 'active'; range: string; minutesToChange: number | null }
  | { status: 'after'; range: string }
  | { status: 'off' }
  | { status: 'unavailable' };

const HHMM = /^(\d{1,2}):(\d{2})$/;

/**
 * Resolve the shift footer state for a site from its weekly working schedule and
 * the current instant. All day/time reasoning happens in the site's own
 * timezone (`workingHours.tz`) so the footer reflects the site's clock, not the
 * viewer's. The "next change" countdown is derived from `now` and so stays live
 * as the component re-renders — this is the part the old hardcoded label faked.
 */
export function deriveShiftState(
  workingHours: IWorkingHours | undefined,
  now: DateTime,
): ShiftState {
  if (!workingHours?.available || !workingHours.tz || workingHours.days.length === 0) {
    return { status: 'unavailable' };
  }

  const sited = now.setZone(workingHours.tz);
  // Luxon weekday is 1=Mon…7=Sun; the domain array is Monday-first (index 0 = Monday).
  const today = workingHours.days[sited.weekday - 1];
  if (!today || today.dailyWorkerHours <= 0 || !today.startDayTime || !today.endDayTime) {
    return { status: 'off' };
  }

  const start = parseHHMM(today.startDayTime, sited);
  const end = parseHHMM(today.endDayTime, sited);
  if (!start || !end) {
    // Times that don't parse are bad upstream data, not a real schedule.
    return { status: 'unavailable' };
  }

  const range = `${start.toFormat('HH:mm')}–${end.toFormat('HH:mm')}`;
  if (end <= start) {
    // Window crosses midnight (e.g. a night shift 22:00–06:00): it's active
    // from the start through midnight to the end — i.e. `now >= start` (this
    // evening) OR `now < end` (the small hours). Outside that is the daytime
    // gap, where the next change is tonight's start. The active branch keeps a
    // null countdown because the end falls on the *next* calendar day; a
    // same-day diff would be misleading.
    if (sited >= start || sited < end) {
      return { status: 'active', range, minutesToChange: null };
    }
    return { status: 'before', range, minutesToChange: minutesBetween(sited, start) };
  }
  if (sited < start) {
    return { status: 'before', range, minutesToChange: minutesBetween(sited, start) };
  }
  if (sited < end) {
    return { status: 'active', range, minutesToChange: minutesBetween(sited, end) };
  }
  return { status: 'after', range };
}

function parseHHMM(value: string, sameDayAs: DateTime): DateTime | null {
  const match = HHMM.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return sameDayAs.set({ hour, minute, second: 0, millisecond: 0 });
}

function minutesBetween(from: DateTime, to: DateTime): number {
  return Math.max(0, Math.round(to.diff(from, 'minutes').minutes));
}
